import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class CartConversionTools extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
    this.addEventListener('submit', this.handleSubmit);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.handleSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
  }

  handleCartUpdate = (event) => {
    event.promise
      ?.then(() => {
        setTimeout(() => {
          document.querySelectorAll('cart-conversion-tools').forEach((element) => {
            if (element instanceof CartConversionTools) element.refreshRecommendations();
          });
        }, 150);
      })
      .catch(() => {});
  };

  refreshRecommendations() {
    const slot = this.querySelector('[data-recommendations-slot]');
    if (!(slot instanceof HTMLElement)) return;
    slot.replaceChildren();
    slot.dataset.loaded = 'false';
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const slot = this.querySelector('[data-recommendations-slot]');
    const productId = this.dataset.productId;
    if (!(slot instanceof HTMLElement) || !productId || slot.dataset.loaded === 'true') return;

    slot.dataset.loaded = 'true';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}?section_id=cart-drawer-recommendations`;
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Recommendations request failed');

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendations = documentFragment.querySelector('.cart-upsells');
      if (recommendations) slot.replaceChildren(recommendations);
    } catch (error) {
      console.warn('[cart-conversion-tools] Unable to load recommendations', error);
    }
  }

  handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-upsell-form]')) return;

    event.preventDefault();

    const select = form.querySelector('select[name="id"]');
    const button = form.querySelector('button[type="submit"]');
    const error = this.querySelector('[data-upsell-error]');
    if (!(select instanceof HTMLSelectElement) || !(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    error?.setAttribute('hidden', '');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ id: select.value, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({
        items: [{ id: Number(select.value), quantity: 1 }],
        sections: Array.from(sectionIds).join(','),
        sections_url: window.location.pathname,
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorText);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error(this.dataset.errorText);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: result.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-conversion-tools',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (error instanceof Error && error.name === 'AbortError') return;
      const message = error instanceof Error ? error.message : this.dataset.errorText;
      const errorNode = this.querySelector('[data-upsell-error]');
      if (errorNode instanceof HTMLElement) {
        errorNode.textContent = message || this.dataset.errorText || '';
        errorNode.removeAttribute('hidden');
      }
      button.disabled = false;
    }
  };
}

if (!customElements.get('cart-conversion-tools')) {
  customElements.define('cart-conversion-tools', CartConversionTools);
}